import { FileText, FileX, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { arquivoDisponivel } from '@/api/http-client';
import { VisualizadorDeDocumento } from '@/features/discussoes/components/VisualizadorDeDocumento';
import { Button } from '@/shared/ui/button';
import { toast } from '@/shared/ui/sonner';

interface Props {
  /** Signed URL do PDF. `null` = a API já sabe que não há arquivo para mostrar. */
  url: string | null;
  rotulo?: string;
  className?: string;
}

/**
 * Botão "Ver PDF original" dos artigos do advisor.
 *
 * Confere se o arquivo existe ANTES de abrir o visualizador: o `<iframe>` não
 * avisa quando a URL responde 404, e o usuário via o JSON de erro da API no
 * lugar do PDF. Indisponível vira um aviso, e o texto do artigo segue legível.
 */
export function VerPdfOriginal({ url, rotulo = 'Ver PDF original', className }: Props) {
  const [aberto, setAberto] = useState(false);
  const [conferindo, setConferindo] = useState(false);
  const [indisponivel, setIndisponivel] = useState(false);

  if (!url || indisponivel) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground ${className ?? ''}`}>
        <FileX className="h-3.5 w-3.5" />
        PDF original indisponível
      </span>
    );
  }

  async function abrir() {
    if (!url) return;
    setConferindo(true);
    try {
      if (await arquivoDisponivel(url)) {
        setAberto(true);
      } else {
        setIndisponivel(true);
        toast.error('O PDF original deste artigo não está disponível no momento.');
      }
    } finally {
      setConferindo(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={`gap-2 ${className ?? ''}`}
        disabled={conferindo}
        onClick={() => void abrir()}
      >
        {conferindo ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        {rotulo}
      </Button>

      <VisualizadorDeDocumento
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        url={url}
        mimeType="application/pdf"
      />
    </>
  );
}
